import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Card, PageHeader, QueryError, Spinner, StatCard, Button } from "../components";
import { useToast } from "../contexts/ToastContext";
import { apiGet, apiPost } from "../lib/api";

type QueuePriority = "critical" | "high" | "medium" | "low";
type QueueStatus = "open" | "snoozed" | "ignored" | "resolved";
type QueueAction = "no_action" | "watch" | "investigate" | "fix_candidate" | "design_candidate";

interface QueueItem {
  id: string;
  dedupeKey: string;
  sourceType: "lab_finding" | "alert" | "backup" | "version_gate";
  sourceRef?: string;
  title: string;
  summary: string;
  priority: QueuePriority;
  recommendedAction: QueueAction;
  status: QueueStatus;
  occurrenceCount: number;
  firstSeenAt: number;
  lastSeenAt: number;
  snoozedUntil?: number;
  payload: Record<string, unknown>;
  shipEligible: boolean;
}

interface QueueResponse {
  items: QueueItem[];
  total: number;
}

const PRIORITY_BADGE: Record<QueuePriority, "red" | "amber" | "blue" | "gray"> = {
  critical: "red",
  high: "red",
  medium: "amber",
  low: "gray",
};

const STATUS_BADGE: Record<QueueStatus, "green" | "red" | "amber" | "gray" | "blue"> = {
  open: "red",
  snoozed: "amber",
  ignored: "gray",
  resolved: "green",
};

function formatTimestamp(value: number | undefined): string {
  if (!value || !Number.isFinite(value)) return "Unknown";
  return new Date(value).toLocaleString();
}

function labelForSource(source: QueueItem["sourceType"]): string {
  switch (source) {
    case "lab_finding":
      return "Lab";
    case "version_gate":
      return "Version";
    case "backup":
      return "Backup";
    default:
      return "Alert";
  }
}

export default function Queue() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<QueueResponse>({
    queryKey: ["autopilot", "queue"],
    queryFn: () => apiGet<QueueResponse>("/api/autopilot/queue"),
    refetchInterval: 30_000,
  });

  const ship = useMutation({
    mutationFn: (id: string) => apiPost(`/api/autopilot/queue/${id}/ship`),
    onSuccess: () => {
      toast.success("Production promotion triggered");
      queryClient.invalidateQueries({ queryKey: ["autopilot", "queue"] });
      queryClient.invalidateQueries({ queryKey: ["versions"] });
    },
    onError: (err: Error) => toast.error("Ship action failed", err.message),
  });

  const snooze = useMutation({
    mutationFn: (id: string) => apiPost(`/api/autopilot/queue/${id}/snooze`, { hours: 24 }),
    onSuccess: () => {
      toast.success("Queue item snoozed for 24h");
      queryClient.invalidateQueries({ queryKey: ["autopilot", "queue"] });
    },
    onError: (err: Error) => toast.error("Snooze failed", err.message),
  });

  const ignore = useMutation({
    mutationFn: (id: string) => apiPost(`/api/autopilot/queue/${id}/ignore`),
    onSuccess: () => {
      toast.success("Queue item ignored");
      queryClient.invalidateQueries({ queryKey: ["autopilot", "queue"] });
    },
    onError: (err: Error) => toast.error("Ignore failed", err.message),
  });

  const items = data?.items ?? [];
  const openCount = items.filter((item) => item.status === "open").length;
  const shipCount = items.filter((item) => item.shipEligible).length;
  const criticalCount = items.filter((item) => item.priority === "critical").length;

  if (error && !data) {
    return (
      <div>
        <PageHeader
          title="Queue"
          subtitle="Autopilot operator inbox generated from labs, health, backups, and version gates"
        />
        <QueryError error={error} queryKeys={[["autopilot", "queue"]]} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Queue"
        subtitle="Autopilot operator inbox generated from labs, health, backups, and version gates"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard title="Open Items" value={String(openCount)} />
        <StatCard title="Shippable" value={String(shipCount)} />
        <StatCard title="Critical" value={String(criticalCount)} />
        <StatCard title="Total Visible" value={String(items.length)} />
      </div>

      <Card>
        {isLoading ? (
          <div className="py-16 flex justify-center">
            <Spinner size={24} />
          </div>
        ) : items.length === 0 ? (
          <div style={{ padding: 36, textAlign: "center", color: "var(--c-text-secondary)" }}>
            No actionable queue items right now.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--c-border)" }}>
                <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600 }}>Item</th>
                <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600 }}>Source</th>
                <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600 }}>
                  Priority
                </th>
                <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600 }}>Status</th>
                <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600 }}>Action</th>
                <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600 }}>
                  Last Seen
                </th>
                <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600 }}>
                  Controls
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const busy = ship.isPending || snooze.isPending || ignore.isPending;
                return (
                  <tr key={item.id} style={{ borderBottom: "1px solid var(--c-border-subtle)" }}>
                    <td style={{ padding: "10px 12px", maxWidth: 420 }}>
                      <div style={{ fontWeight: 600, color: "var(--c-text)", marginBottom: 4 }}>
                        {item.title}
                      </div>
                      <div
                        style={{
                          color: "var(--c-text-secondary)",
                          fontSize: 12,
                          lineHeight: 1.45,
                        }}
                      >
                        {item.summary}
                      </div>
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <Badge color="blue">{labelForSource(item.sourceType)}</Badge>
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <Badge color={PRIORITY_BADGE[item.priority]} dot>
                        {item.priority}
                      </Badge>
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <Badge color={STATUS_BADGE[item.status]}>{item.status}</Badge>
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <Badge color="amber">{item.recommendedAction.replaceAll("_", " ")}</Badge>
                    </td>
                    <td style={{ padding: "10px 12px", color: "var(--c-text-secondary)" }}>
                      {formatTimestamp(item.lastSeenAt)}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        {item.shipEligible ? (
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => ship.mutate(item.id)}
                            disabled={busy}
                          >
                            Ship
                          </Button>
                        ) : null}
                        {item.status === "open" ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => snooze.mutate(item.id)}
                            disabled={busy}
                          >
                            Snooze
                          </Button>
                        ) : null}
                        {item.status !== "ignored" ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => ignore.mutate(item.id)}
                            disabled={busy}
                          >
                            Ignore
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
